import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import ProtectedRoute from "./ProtectedRoutes";

import AppLayout from "../components/app/AppLayout";

import LandingPage from "../pages/LandingPage";
import LoginPage from "../pages/auth/LoginPage";
import ForgotPasswordPage from "../pages/auth/ForgotPasswordPage";
import SetPasswordPage from "../pages/auth/SetPasswordFromInvite";
import ResetPasswordPage from "../pages/auth/ResetPasswordPage";
import ShipmentPublicView from "../pages/shipments/ShipmentPublicView";
import DriverPOHSignPage from "../pages/poh/DriverPOHSignPage";
import DriverDeliveryPOHSignPage from "../pages/poh/DriverDeliveryPOHSignPage";

import DashboardPage from "../pages/dashboard/DashboardPage";
import ProfilePage from "../pages/users/ProfilePage";
import { ShipmentsList } from "../pages/shipments/ShipmentsList";
import { ShipmentPage } from "../pages/shipments/ShipmentPage";
import { DriversShipments } from "../pages/shipments/DriversShipments";
import DriverView from "../pages/drivers/DriverView";
import { UsersList } from "../pages/users/UsersList";
import { DriversList } from "../pages/users/DriversList";
import HelpCenter from "../pages/HelpCenter";
import { PartnersListing } from "../pages/partners/PartnersListing";
import { AddressesListing } from "../pages/addresses/AddressesListing";
import { VehiclesListing } from "../pages/vehicles";
import { TruckGroupsPage } from "../pages/truckGroups";
import { ServicesListing } from "../pages/services";
import { CostServicesListing } from "../pages/costServices";
/**
 * The shipment gantt, kept and no longer routed to.
 *
 * `/operations-calendar` now opens the container planning board below. The
 * gantt's page and components are left in place untouched — it is the only
 * screen that draws shipments against a time axis, and the module it belongs to
 * is unchanged — but planning is containers now, and one URL cannot be two
 * screens. Restoring it is a one-line change here.
 */
// import { OperationsCalendarPage } from "../pages/calendar";
import ContainerPlanningPage from "../pages/planning/ContainerPlanningPage";
import TruckReportPage from "../pages/reports/TruckReportPage";
import { ShipmentsGroupList } from "../pages/groups";
import { TasksListing } from "../pages/tasks/TasksListing";
import { WorksListPage } from "../pages/works/WorksListPage";
import OrganisationSettings from "../pages/settings/OrganisationSettings";
import IntegrationsPage from "../pages/settings/IntegrationsPage";
import OrdersList from "../pages/orders/OrdersList";
import OrderDetailPage from "../pages/orders/OrderDetailPage";
import ContainerOrdersList from "../pages/containerOrders/ContainerOrdersList";
import ContainerOrderIntakeList from "../pages/containerOrderIntake/ContainerOrderIntakeList";
import ContainerOrderIntakeReview from "../pages/containerOrderIntake/ContainerOrderIntakeReview";
import ContainerOrderPage from "../pages/containerOrders/ContainerOrderPage";
import ContainerLinePage from "../pages/containerOrders/ContainerLinePage";
import DriverContainersListPage from "../pages/containerOrders/DriverContainersListPage";
import TruckDevicePage from "../pages/devices/TruckDevicePage";
import { hasDeviceSession } from "../utils/deviceSession";
import DriverContainerExecutionPage from "../pages/containerOrders/DriverContainerExecutionPage";
import InvoicesListPage from "../pages/invoices/InvoicesListPage";
import InvoiceEditor from "../pages/invoices/InvoiceEditor";
import InvoiceDetail from "../pages/invoices/InvoiceDetail";
import DriverWorksListPage from "../pages/works/DriverWorksListPage";
import DriverWorkExecutionPage from "../pages/works/DriverWorkExecutionPage";
import DriverHome from "../pages/drivers/DriverHome";

import SuperAdminRoutes from "./SuperAdminRoutes";

export const getPortalType = () => {
  const hostname = window.location.hostname;
  const hostParts = hostname.split(".");
  const subdomain = hostParts.length > 2 ? hostParts[0] : null;

  return {
    isAdminPortal: subdomain === "admin",
    isTenantPortal: subdomain && subdomain !== "admin",
    subdomain,
  };
};

export const getSubdomainInfo = () => {
  const hostname = window.location.hostname;
  const hostParts = hostname.split(".");

  const isSubdomainPresent = hostParts.length > 2;
  const subdomain = isSubdomainPresent ? hostParts[0] : null;

  const isProduction = import.meta.env.MODE === "production";

  return { isProduction, isSubdomainPresent, subdomain };
};

/**
 * ROUTE TABLE.
 *
 * Every module route is declared unconditionally and gated by ProtectedRoute,
 * rather than being added to or removed from this tree per tenant. That is
 * deliberate: a route that is not declared falls through to the "*" catch-all
 * and dumps a signed-in user on the public landing page, which reads as the app
 * breaking. A declared-but-gated route sends them to their own home screen
 * instead — the same thing a mistyped URL does, and the same thing every other
 * tenant sees for a module they do not have.
 *
 * Where a path serves both audiences (shipments, works), the branch picks the
 * component AND the module key by role, because the office console and the
 * driver execution screen are separate modules over the same data: a company
 * can run works in the office without opening the driver app.
 *
 * None of this is the access control. backend/middlewares/moduleGuard.js is,
 * and it answers 404 for anything disabled no matter what this file renders.
 */
const AppRoutes: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { isSubdomainPresent } = getSubdomainInfo();

  const { isAdminPortal } = getPortalType();

  if (isAdminPortal) {
    return <SuperAdminRoutes />;
  }
  const canAccessProtectedRoutes = isSubdomainPresent;

  const isDriver = user?.role === "driver";

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />

      <Route path="/shipment-drivers-view" element={<DriverView />} />

      <Route path="/shipment-clients-view" element={<ShipmentPublicView />} />
      <Route path="/poh/driver-sign/:qrToken" element={<DriverPOHSignPage />} />
      <Route
        path="/poh/delivery-driver-sign/:qrToken"
        element={<DriverDeliveryPOHSignPage />}
      />

      {/*
        The screen in a truck's cab.

        Outside the authenticated block on purpose: a tablet arriving here has no
        user and, the first time, no pairing either — signing in is the whole
        point of the page. It still needs a tenant, which the subdomain supplies,
        and the device secret it stores is what every request from that tablet is
        actually scoped by.

        Three cases, and the middle one is the one worth stating:

          a live truck session   already signed in on this screen — go to the
                                 work, so returning to this URL mid-shift does
                                 not present an empty keypad over a live session
          any other session      somebody signed in with a password. Pairing is
                                 an act of installing a screen in a truck, not
                                 something a signed-in person does from inside
                                 the app, so they are sent back to their own
                                 landing page rather than shown a pairing form
                                 they have no business with
          nobody signed in       the tablet itself — pair, or enter a PIN
      */}
      {canAccessProtectedRoutes && (
        <Route
          path="/truck"
          element={
            !isAuthenticated ? (
              <TruckDevicePage />
            ) : hasDeviceSession() ? (
              <Navigate to="/driver/containers" replace />
            ) : (
              <Navigate
                to={isDriver ? "/driver-home" : "/dashboard"}
                replace
              />
            )
          }
        />
      )}

      {canAccessProtectedRoutes && (
        <>
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />
            }
          />
          <Route
            path="/forgot-password"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" />
              ) : (
                <ForgotPasswordPage />
              )
            }
          />
          <Route
            path="/reset-password"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" />
              ) : (
                <ResetPasswordPage />
              )
            }
          />
          <Route
            path="/set-password"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" />
              ) : (
                <SetPasswordPage />
              )
            }
          />
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="dashboard">
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route path="/profile" element={<ProfilePage />} />
            {/* Driver landing. Ungated on purpose — it is where every other
                driver route redirects to, so gating it could bounce a driver
                between two disabled screens. The tiles it offers are filtered
                by the driver modules this tenant actually has. */}
            <Route
              path="/driver-home"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <DriverHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute roles={["admin"]} module="users">
                  <UsersList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/drivers"
              element={
                <ProtectedRoute roles={["admin"]} module="drivers">
                  <DriversList />
                </ProtectedRoute>
              }
            />

            {/* ── Shipments ──────────────────────────────────────────────── */}
            <Route
              path="/shipments"
              element={
                isDriver ? (
                  <ProtectedRoute roles={["driver"]} module="driverShipments">
                    <DriversShipments />
                  </ProtectedRoute>
                ) : (
                  <ProtectedRoute roles={["admin", "user"]} module="shipments">
                    <ShipmentsList />
                  </ProtectedRoute>
                )
              }
            />
            <Route
              path="/shipments/:id"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="shipments">
                  <ShipmentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shipments-group"
              element={
                <ProtectedRoute
                  roles={["admin", "user"]}
                  module="shipmentGroups"
                >
                  <ShipmentsGroupList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/tasks"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="tasks">
                  <TasksListing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/:id"
              element={
                <ProtectedRoute roles={["driver"]} module="driverShipments">
                  <DriverView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partners"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="partners">
                  <PartnersListing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/addresses"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="addresses">
                  <AddressesListing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vehicles"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="vehicles">
                  <VehiclesListing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/truck-groups"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="vehicles">
                  <TruckGroupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/services"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="services">
                  <ServicesListing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cost-services"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="costServices">
                  <CostServicesListing />
                </ProtectedRoute>
              }
            />
            {/* Either module opens the board — see routes/planning.js. */}
            <Route
              path="/operations-calendar"
              element={
                <ProtectedRoute
                  roles={["admin", "user"]}
                  anyOf={["calendar", "containerOrders"]}
                >
                  <ContainerPlanningPage />
                </ProtectedRoute>
              }
            />

            {/* ── Works ──────────────────────────────────────────────────── */}
            <Route
              path="/works"
              element={
                isDriver ? (
                  <ProtectedRoute roles={["driver"]} module="driverWorks">
                    <DriverWorksListPage />
                  </ProtectedRoute>
                ) : (
                  <ProtectedRoute roles={["admin", "user"]} module="works">
                    <WorksListPage />
                  </ProtectedRoute>
                )
              }
            />
            {/* Execution is the driver screen only; a dispatcher opens a work
                from the console list, so they go back there. */}
            <Route
              path="/works/:id"
              element={
                isDriver ? (
                  <ProtectedRoute roles={["driver"]} module="driverWorks">
                    <DriverWorkExecutionPage />
                  </ProtectedRoute>
                ) : (
                  <Navigate to="/works" replace />
                )
              }
            />

            <Route
              path="/organisation-settings"
              element={
                <ProtectedRoute roles={["admin"]} module="organisationSettings">
                  <OrganisationSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrations"
              element={
                <ProtectedRoute roles={["admin"]} module="integrations">
                  <IntegrationsPage />
                </ProtectedRoute>
              }
            />

            {/* ── SO/PO orders (Dynamics 365) ────────────────────────────── */}
            <Route
              path="/orders"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="orders">
                  <OrdersList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/:orderId"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="orders">
                  <OrderDetailPage />
                </ProtectedRoute>
              }
            />

            {/* ── Container orders ───────────────────────────────────────── */}
            <Route
              path="/container-reports"
              element={
                <ProtectedRoute
                  roles={["admin", "user"]}
                  module="containerOrders"
                >
                  <TruckReportPage />
                </ProtectedRoute>
              }
            />
            {/* Transport orders received from Oliver, reviewed before they
                become container orders. Same audience and module as creating a
                container order by hand. */}
            <Route
              path="/order-intake"
              element={
                <ProtectedRoute
                  roles={["admin", "user"]}
                  module="containerOrders"
                >
                  <ContainerOrderIntakeList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/order-intake/:intakeId"
              element={
                <ProtectedRoute
                  roles={["admin", "user"]}
                  module="containerOrders"
                >
                  <ContainerOrderIntakeReview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/container-orders"
              element={
                <ProtectedRoute
                  roles={["admin", "user"]}
                  module="containerOrders"
                >
                  <ContainerOrdersList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/container-orders/new"
              element={
                <ProtectedRoute
                  roles={["admin", "user"]}
                  module="containerOrders"
                >
                  <ContainerOrderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/container-orders/:orderId"
              element={
                <ProtectedRoute
                  roles={["admin", "user"]}
                  module="containerOrders"
                >
                  <ContainerOrderPage />
                </ProtectedRoute>
              }
            />
            {/* One container, on its own page. Nested under its order because
                that is what it belongs to — and because both ids are needed to
                fetch it: a container is only ever read through its order, which
                is what scopes it to the company. */}
            <Route
              path="/container-orders/:orderId/container/:containerId"
              element={
                <ProtectedRoute
                  roles={["admin", "user"]}
                  module="containerOrders"
                >
                  <ContainerLinePage />
                </ProtectedRoute>
              }
            />
            {/* Driver-side container execution. Kept under /driver/ rather than
                /container-orders/ because a driver never sees the order itself,
                only the containers on the truck they signed in on — and it is
                its own module, so a tenant can run container operations in the
                office without opening the driver app. */}
            {/*
                Two gates, and they answer different questions.

                The module says whether this tenant has the driver container app
                at all; the truck session says whether *this* sign-in can reach
                it. Containers are dispatched to a truck and worked from the
                screen in its cab, so a driver signed in with their email and
                password has no truck — the server refuses every container
                endpoint to them, and showing the pages anyway would be a list
                that could only ever be empty and buttons that could only ever
                fail.

                Ordered device-first only because it is the cheaper test; the
                module guard is the authoritative one and is mirrored server-side
                by `requireModule` on routes/containerOrders.js. Both have to
                pass. A driver without a truck session is sent to their own
                landing page rather than to `/truck`, which would offer to pair a
                screen they are not installing. */}
            <Route
              path="/driver/containers"
              element={
                !isDriver ? (
                  <Navigate to="/container-orders" />
                ) : !hasDeviceSession() ? (
                  <Navigate to="/driver-home" replace />
                ) : (
                  <ProtectedRoute roles={["driver"]} module="driverContainers">
                    <DriverContainersListPage />
                  </ProtectedRoute>
                )
              }
            />
            <Route
              path="/driver/containers/:lineId"
              element={
                !isDriver ? (
                  <Navigate to="/container-orders" />
                ) : !hasDeviceSession() ? (
                  <Navigate to="/driver-home" replace />
                ) : (
                  <ProtectedRoute roles={["driver"]} module="driverContainers">
                    <DriverContainerExecutionPage />
                  </ProtectedRoute>
                )
              }
            />

            {/* ── Invoicing ──────────────────────────────────────────────── */}
            <Route
              path="/invoices"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="invoices">
                  <InvoicesListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices/new"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="invoices">
                  <InvoiceEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices/:id"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="invoices">
                  <InvoiceDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices/:id/edit"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="invoices">
                  <InvoiceEditor />
                </ProtectedRoute>
              }
            />

            <Route
              path="/help"
              element={
                <ProtectedRoute roles={["admin", "user"]} module="helpCenter">
                  <HelpCenter />
                </ProtectedRoute>
              }
            />
          </Route>
        </>
      )}

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

export default AppRoutes;
